import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Mock API - replace with actual API calls
const api = {
  async fetchDevices(page = 1, limit = 10, search = '', filter = '') {
    await new Promise((resolve) => setTimeout(resolve, 800));
    const allDevices = [
      {
        id: 1,
        name: 'EliteBook',
        serialNo: '192.168.0.1',
        model: 'Hp',
        operatingSystem: 'Windows',
        type: 'Laptop',
        status: 'Healthy',
        assignedTo: {
          name: 'Ralph Edwards',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'laptop',
      },
      {
        id: 2,
        name: 'Hp Envy',
        serialNo: '192.168.0.1',
        model: 'Hp',
        operatingSystem: 'Windows',
        type: 'Laptop',
        status: 'Faulty',
        assignedTo: {
          name: 'Dianne Russell',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'laptop',
      },
      {
        id: 3,
        name: 'EliteDesk',
        serialNo: '192.168.0.1',
        model: 'Lenovo',
        operatingSystem: 'Linux',
        type: 'Desktop',
        status: 'On maintenance',
        assignedTo: {
          name: 'Darrell Steward',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'desktop',
      },
      {
        id: 4,
        name: 'Quantum',
        serialNo: '192.168.0.1',
        model: 'Mac book',
        operatingSystem: 'Mac OS',
        type: 'Desktop',
        status: 'On maintenance',
        assignedTo: {
          name: 'Ronald Richards',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'desktop',
      },
      {
        id: 5,
        name: 'ASUS 123',
        serialNo: 'FM101',
        model: 'ASUS',
        operatingSystem: 'Windows',
        type: 'Desktop',
        status: 'Healthy',
        assignedTo: {
          name: 'Darrell Steward',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'desktop',
      },
      {
        id: 6,
        name: 'Toshiba Tecra',
        serialNo: 'FM101',
        model: 'Toshiba',
        operatingSystem: 'Windows',
        type: 'Laptop',
        status: 'Faulty',
        assignedTo: {
          name: 'Ronald Richards',
          avatar: '/placeholder.svg?height=32&width=32',
        },
        icon: 'laptop',
      },
    ];
    
    let filteredDevices = allDevices;
    if (search) {
      filteredDevices = filteredDevices.filter(
        (device) =>
          device.name.toLowerCase().includes(search.toLowerCase()) ||
          device.model.toLowerCase().includes(search.toLowerCase()) ||
          device.assignedTo.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (filter && filter !== 'all') {
      filteredDevices = filteredDevices.filter(
        (device) => device.status.toLowerCase().replace(' ', '-') === filter
      );
    }
    
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedDevices = filteredDevices.slice(startIndex, endIndex);
    
    return {
      devices: paginatedDevices,
      total: filteredDevices.length,
      totalPages: Math.ceil(filteredDevices.length / limit),
      currentPage: page,
    };
  },
  async addDevice(deviceData) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return { success: true, message: 'Device added successfully' };
  },
  async updateDevice(deviceId, deviceData) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return { success: true, message: 'Device updated successfully' };
  },
  async removeDevice(deviceId) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return { success: true, message: 'Device removed successfully' };
  },
  async bulkRemoveDevices(deviceIds) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return { success: true, message: 'Devices removed successfully' };
  },
};

// Async thunks
export const fetchDevices = createAsyncThunk(
  'devices/fetchDevices',
  async ({ page, limit, search, filter }) => {
    const response = await api.fetchDevices(page, limit, search, filter);
    return response;
  }
);

export const addDevice = createAsyncThunk(
  'devices/addDevice',
  async (deviceData) => {
    const response = await api.addDevice(deviceData);
    return response;
  }
);

export const updateDevice = createAsyncThunk(
  'devices/updateDevice',
  async ({ deviceId, deviceData }) => {
    const response = await api.updateDevice(deviceId, deviceData);
    return { deviceId, deviceData, ...response };
  }
);

export const removeDevice = createAsyncThunk(
  'devices/removeDevice',
  async (deviceId) => {
    const response = await api.removeDevice(deviceId);
    return { deviceId, ...response };
  }
);

export const bulkRemoveDevices = createAsyncThunk(
  'devices/bulkRemoveDevices',
  async (deviceIds) => {
    const response = await api.bulkRemoveDevices(deviceIds);
    return { deviceIds, ...response };
  }
);

const devicesSlice = createSlice({
  name: 'devices',
  initialState: {
    items: [],
    currentPage: 1,
    totalPages: 1,
    total: 0,
    searchTerm: '',
    filter: 'all',
    selectedDevices: [],
    loading: false,
    error: null,
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSelectedDevices: (state, action) => {
      state.selectedDevices = action.payload;
    },
    toggleDeviceSelection: (state, action) => {
      const deviceId = action.payload;
      const index = state.selectedDevices.indexOf(deviceId);
      if (index > -1) {
        state.selectedDevices.splice(index, 1);
      } else {
        state.selectedDevices.push(deviceId);
      }
    },
    selectAllDevices: (state) => {
      state.selectedDevices = state.items.map((device) => device.id);
    },
    clearSelectedDevices: (state) => {
      state.selectedDevices = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch devices
      .addCase(fetchDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.devices;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add device
      .addCase(addDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDevice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update device
      .addCase(updateDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDevice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Remove device
      .addCase(removeDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeDevice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Bulk remove devices
      .addCase(bulkRemoveDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkRemoveDevices.fulfilled, (state) => {
        state.loading = false;
        state.selectedDevices = [];
      })
      .addCase(bulkRemoveDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setSearchTerm,
  setFilter,
  setCurrentPage,
  setSelectedDevices,
  toggleDeviceSelection,
  selectAllDevices,
  clearSelectedDevices,
} = devicesSlice.actions;

export default devicesSlice.reducer;
